/**
 * KNK-1.12 — reading and changing Dispositions and quick buttons for the active account.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. What a row means is `lib/knockDispositions.ts`; who may change
 * what, and what may never change, is the database's (migration 20261005a8). Errors are thrown
 * with the server's own message: every refusal there is a sentence written for this reader
 * ("…has been used, so it can be archived but not deleted").
 *
 * Reads rely on RLS for the account: `pin_status_config` and `knock_quick_buttons` are both
 * scoped to `get_current_user_tenant_id()`, which is the switched-to account for a superadmin.
 */

import { supabase } from '@/lib/supabaseClient';
import {
  parseDisposition,
  parseQuickButtonRows,
  type Disposition,
  type DispositionPatch,
  type QuickButtonRow,
} from '@/lib/knockDispositions';

const fail = (error: { message: string } | null) => {
  if (error) throw new Error(error.message);
};

/** Archived rows come back only to a field.settings.manage holder, which is who this is for. */
export async function fetchDispositions(): Promise<Disposition[]> {
  const { data, error } = await supabase
    .from('pin_status_config')
    .select('id, status_code, display_name, category, color_hex, sort_order, is_active, requires_notes, creates_lead, revisit_offset_hours, terminal_days, door_fact, door_fact_days')
    .order('sort_order')
    .order('display_name');
  fail(error);
  return (data ?? []).map(parseDisposition).filter((d): d is Disposition => d !== null);
}

export async function updateDisposition(id: string, patch: Partial<DispositionPatch> & { is_active?: boolean }): Promise<void> {
  const { data, error } = await supabase.from('pin_status_config').update(patch).eq('id', id).select('id');
  fail(error);
  // RLS refuses an UPDATE by matching nothing. Say so instead of reporting a save.
  if (!data || data.length === 0) throw new Error('Not saved: you do not have permission to change Dispositions in this account.');
}

export async function createDisposition(tenantId: string, code: string, patch: DispositionPatch): Promise<void> {
  // sort_order is filled in by pin_status_config_rules_guard (after the last one).
  const { error } = await supabase.from('pin_status_config').insert({ tenant_id: tenantId, status_code: code, ...patch });
  fail(error);
}

export async function deleteDisposition(id: string): Promise<void> {
  const { data, error } = await supabase.from('pin_status_config').delete().eq('id', id).select('id');
  fail(error);
  if (!data || data.length === 0) throw new Error('Not deleted: you do not have permission to change Dispositions in this account.');
}

export async function fetchQuickButtons(): Promise<QuickButtonRow[]> {
  const { data, error } = await supabase.from('knock_quick_buttons').select('team_id, slot, status_code').order('slot');
  fail(error);
  return parseQuickButtonRows(data);
}

/** Replaces one scope's whole set. `teamId` null is the Account; an empty team set reverts it. */
export async function setQuickButtons(teamId: number | null, codes: string[]): Promise<void> {
  const { error } = await supabase.rpc('set_knock_quick_buttons', { p_team_id: teamId, p_codes: codes });
  fail(error);
}

export interface TeamOption {
  id: number;
  name: string;
}

export async function fetchTeams(): Promise<TeamOption[]> {
  const { data, error } = await supabase.from('teams').select('id, name').order('name');
  fail(error);
  return (data ?? []) as TeamOption[];
}
